import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './routes/PrivateRoute';
import DashboardLayout from './layouts/DashboardLayout';
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import UserProfile from './pages/UserProfile';
import AdminDashboard from './pages/admin/AdminDashboard';
import KloudOpsDashboard from './pages/kloud-ops/KloudOpsDashboard';
import CalendarioTurnos from './pages/kloud-ops/CalendarioTurnos';
import TicketsControlPage from './pages/kloud-ops/TicketsControlPage';
import CentroOperaciones from './pages/kloud-ops/CentroOperaciones';
import CentroReportes from './pages/kloud-ops/CentroReportes';
import ClientesOpsDashboard from './pages/kloud-ops/ClientesOpsDashboard';
import CoberturaOpsPage from './pages/kloud-ops/CoberturaOpsPage';
import DashboardFusion from './pages/kloud-ops/DashboardFusion';
import OpsProfitabilityPage from './pages/kloud-ops/profitability/OpsProfitabilityPage';
import InnovationDashboardFusion from './pages/innovation/DashboardFusionPage';
import KloudSaaSDashboard from './pages/kloud-saas/KloudSaaSDashboard';
import MesaSpinDashboard from './pages/mesa-spin/MesaSpinDashboard';
import MesaSpinObjetivos from './pages/mesa-spin/MesaSpinObjetivos';
import FinopsDashboard from './pages/finops/FinopsDashboard';
import DataQualityMonitor from './pages/finops/DataQualityMonitor';
import QADashboard from './pages/qa/QADashboard';
import { IaCGeneratorPage, DeployedProjectsPage, DeploymentProjectDetailPage, IaCRepositoriesPage, DeploymentSettingsPage } from './pages/servicios-profesionales/KloudDeploymentPages';
import ReporteSemanalPage from './pages/servicios-profesionales/ReporteSemanalPage';
import PendingApproval from './pages/PendingApproval';
import TimesheetsDashboard from './pages/timesheets/TimesheetsDashboard';
import BillingDashboard from './pages/billing/BillingDashboard';
import PresupuestosDashboard from './pages/finops/PresupuestosDashboard';
import RenovacionesDashboard from './pages/finops/RenovacionesDashboard';
import ServiciosCloudPage from './pages/finops/servicios-cloud/ServiciosCloudPage';
import VentasAgentPage from './pages/ventas/VentasAgentPage';
import CloudAccountsPage from './pages/gobierno-cloud/CloudAccountsPage';
import SeguimientoAlianzasPage from './pages/alianzas/SeguimientoAlianzasPage';
import CertificacionesPage from './pages/alianzas/CertificacionesPage';
import PublicCloudQBRPage from './pages/alianzas/PublicCloudQBRPage';
import QBRManagementPage from './pages/alianzas/QBRManagementPage';
import AutomatizacionesPage from './pages/innovation/AutomatizacionesPage';
import ProyectosPage from './pages/innovation/ProyectosPage';

import { AREAS } from './contexts/AuthContext';

function AppRoutes() {
  const { isAuthenticated, userAreas, hasRoles, hasPermission } = useAuth();
  const initialPath = userAreas[0] === AREAS.FINOPS && !hasPermission('finops.dashboard') && hasPermission('finops.presupuestos')
    ? '/finops/presupuestos'
    : userAreas[0] ? `/${userAreas[0]}` : '/admin';

  return (
    <Routes>

      {/* LOGIN */}
      <Route path="/login" element={
        isAuthenticated 
          ? hasRoles 
            ? <Navigate to={initialPath} replace />
            : <Navigate to="/pending-approval" replace />
          : <Login />
      } />

      {/* PENDING */}
      <Route path="/pending-approval" element={
        isAuthenticated 
          ? !hasRoles 
            ? <PendingApproval />
            : <Navigate to={initialPath} replace />
          : <Navigate to="/login" replace />
      } />

      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* PROFILE */}
      <Route path="/profile" element={
        isAuthenticated 
          ? <DashboardLayout><UserProfile /></DashboardLayout>
          : <Navigate to="/login" replace />
      } />

      {/* ADMIN */}
      <Route path="/admin" element={
        <PrivateRoute requiredArea={AREAS.ADMIN}>
          <DashboardLayout>
            <AdminDashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      {/* ===================== */}
      {/* ALIANZAS */}
      {/* ===================== */}
      <Route path="/alianzas">
        <Route index element={<Navigate to="/alianzas/qbr" replace />} />

        <Route path="qbr" element={
          <PrivateRoute
            requiredArea={AREAS.ALIANZAS}
            requiredPermission="alianzas.qbr.view"
          >
            <DashboardLayout>
              <PublicCloudQBRPage />
            </DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="qbr-gestion" element={
          <PrivateRoute
            requiredArea={AREAS.ALIANZAS}
            requiredPermission="alianzas.qbr.gestion.view"
          >
            <DashboardLayout>
              <QBRManagementPage />
            </DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="seguimiento" element={
          <PrivateRoute
            requiredArea={AREAS.ALIANZAS}
            requiredPermission="alianzas.seguimiento.view"
          >
            <DashboardLayout>
              <SeguimientoAlianzasPage />
            </DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="certificaciones" element={
          <PrivateRoute
            requiredArea={AREAS.ALIANZAS}
            requiredPermission="alianzas.certificaciones.view"
          >
            <DashboardLayout>
              <CertificacionesPage />
            </DashboardLayout>
          </PrivateRoute>
        } />
      </Route>

      {/* ===================== */}
      {/* KLOUD OPS */}
      {/* ===================== */}
      <Route path="/kloud-ops">
        <Route index element={<Navigate to="/kloud-ops/dashboard" replace />} />

        <Route path="dashboard" element={
          <PrivateRoute 
            requiredArea={AREAS.KLOUD_OPS}
            requiredPermission="ops.dashboard.basic"
          >
            <DashboardLayout>
              <KloudOpsDashboard />
            </DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="clientes" element={
          <PrivateRoute requiredArea={AREAS.KLOUD_OPS} requiredPermission="ops.dashboard.basic">
            <DashboardLayout><ClientesOpsDashboard /></DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="dashboard-fusion" element={
          <PrivateRoute
            requiredArea={AREAS.KLOUD_OPS}
            requiredPermission="ops.dashboard.fusion.view"
            allowedRoles={['admin', 'kloud_ops_coordinador', 'kloud_ops_gerente']}
          >
            <DashboardLayout><DashboardFusion /></DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="rentabilidad" element={
          <PrivateRoute requiredArea={AREAS.KLOUD_OPS} requiredPermission="ops.dashboard.fusion.view"
            allowedRoles={['admin', 'kloud_ops_coordinador', 'kloud_ops_gerente']}>
            <DashboardLayout><OpsProfitabilityPage /></DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="cobertura" element={
          <PrivateRoute requiredArea={AREAS.KLOUD_OPS} requiredPermission="ops.dashboard.basic">
            <DashboardLayout><CoberturaOpsPage /></DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="centro-operaciones" element={
          <PrivateRoute
            requiredArea={AREAS.KLOUD_OPS}
          >
            <DashboardLayout>
              <CentroOperaciones />
            </DashboardLayout>
          </PrivateRoute>
        } />

        {/* Rutas legacy: redirigen al Centro de Operaciones unificado */}
        <Route path="inventario" element={<Navigate to="/kloud-ops/centro-operaciones" replace />} />
        <Route path="metricas-live" element={<Navigate to="/kloud-ops/centro-operaciones" replace />} />
        <Route path="alertas" element={<Navigate to="/kloud-ops/centro-operaciones" replace />} />

        <Route path="horas" element={
          <PrivateRoute 
            requiredArea={AREAS.KLOUD_OPS}
            requiredPermission="ops.horas.view"
          >
            <DashboardLayout>
              <CalendarioTurnos /> 
            </DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="centro-reportes" element={
          <PrivateRoute
            requiredArea={AREAS.KLOUD_OPS}
          >
            <DashboardLayout>
              <CentroReportes />
            </DashboardLayout>
          </PrivateRoute>
        } />

        {/* Rutas legacy: redirigen al Centro de Reportes unificado */}
        <Route path="monitoreo" element={<Navigate to="/kloud-ops/centro-reportes" replace />} />
        <Route path="reportes-drs" element={<Navigate to="/kloud-ops/centro-reportes" replace />} />

        <Route path="tickets-control" element={
          <PrivateRoute
            requiredArea={AREAS.KLOUD_OPS}
            requiredPermission="ops.tickets-control.view"
          >
            <DashboardLayout>
              <TicketsControlPage />
            </DashboardLayout>
          </PrivateRoute>
        } />

      </Route>

      {/* KLOUD SAAS */}
      <Route path="/kloud-saas" element={
        <PrivateRoute requiredArea={AREAS.KLOUD_SAAS}>
          <DashboardLayout>
            <KloudSaaSDashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      {/* ===================== */}
      {/* MESA SPIN */}
      {/* ===================== */}
      <Route path="/mesa-spin/dashboard" element={
        <PrivateRoute requiredArea={AREAS.MESA_SPIN}>
          <DashboardLayout>
            <MesaSpinDashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      <Route path="/mesa-spin/objetivos" element={
        <PrivateRoute requiredArea={AREAS.MESA_SPIN}>
          <DashboardLayout>
            <MesaSpinObjetivos />
          </DashboardLayout>
        </PrivateRoute>
      } />

      {/* ===================== */}
      {/* FINOPS (YA CON PERMISOS) */}
      {/* ===================== */}

      <Route path="/finops" element={hasPermission('finops.presupuestos') && !hasPermission('finops.dashboard')
        ? <Navigate to="/finops/presupuestos" replace /> :
        <PrivateRoute 
          requiredArea={AREAS.FINOPS}
          requiredPermission="finops.dashboard"
        >
          <DashboardLayout>
            <FinopsDashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      <Route path="/finops/dashboard" element={
        <PrivateRoute 
          requiredArea={AREAS.FINOPS}
          requiredPermission="finops.dashboard"
        >
          <DashboardLayout>
            <FinopsDashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      <Route path="/finops/renovaciones" element={
        <PrivateRoute 
          requiredArea={AREAS.FINOPS}
          requiredPermission="finops.renovaciones"
        >
          <DashboardLayout>
            <RenovacionesDashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      <Route path="/finops/timesheets" element={
        <PrivateRoute 
          requiredArea={AREAS.FINOPS}
          requiredPermission="finops.timesheets"
        >
          <DashboardLayout>
            <TimesheetsDashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      <Route path="/finops/billing" element={
        <PrivateRoute 
          requiredArea={AREAS.FINOPS}
          requiredPermission="finops.dashboard"
        >
          <DashboardLayout>
            <BillingDashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      <Route path="/finops/presupuestos" element={
        <PrivateRoute requiredArea={AREAS.FINOPS} requiredPermission="finops.presupuestos">
          <DashboardLayout><PresupuestosDashboard /></DashboardLayout>
        </PrivateRoute>
      } />

      <Route path="/finops/servicios-cloud" element={
        <PrivateRoute requiredArea={AREAS.FINOPS} requiredPermission="finops.servicios_cloud">
          <DashboardLayout><ServiciosCloudPage /></DashboardLayout>
        </PrivateRoute>
      } />

      <Route path="/finops/calidad-datos" element={
        <PrivateRoute 
          requiredArea={AREAS.FINOPS}
          requiredPermission="finops.calidad"
        >
          <DashboardLayout>
            <DataQualityMonitor />
          </DashboardLayout>
        </PrivateRoute>
      } />

      {/* ===================== */}
      {/* QA */}
      {/* ===================== */}
      <Route path="/qa" element={
        <PrivateRoute requiredArea={AREAS.QA}>
          <DashboardLayout>
            <QADashboard />
          </DashboardLayout>
        </PrivateRoute>
      } />

      {/* ===================== */}
      {/* SERVICIOS */}
      {/* ===================== */}
      <Route path="/servicios-profesionales" element={
        <PrivateRoute requiredArea={AREAS.SERVICIOS_PROFESIONALES}>
          <Navigate to="/servicios-profesionales/iac-generator" replace />
        </PrivateRoute>
      } />
      {[
        ['reporte-semanal', ReporteSemanalPage],
        ['iac-generator', IaCGeneratorPage],
        ['proyectos-desplegados', DeployedProjectsPage],
        ['proyectos-desplegados/:projectId', DeploymentProjectDetailPage],
        ['repositorios-iac', IaCRepositoriesPage],
        ['configuracion', DeploymentSettingsPage],
      ].map(([path, Page]) => (
        <Route key={path} path={`/servicios-profesionales/${path}`} element={
          <PrivateRoute requiredArea={AREAS.SERVICIOS_PROFESIONALES}>
            <DashboardLayout><Page /></DashboardLayout>
          </PrivateRoute>
        } />
      ))}

      {/* ===================== */}
      {/* VENTAS */}
      {/* ===================== */}
      <Route path="/ventas/agente" element={
        <PrivateRoute 
          requiredArea={AREAS.VENTAS}
          requiredPermission="ventas.agent.view"
        >
          <DashboardLayout>
            <VentasAgentPage />
          </DashboardLayout>
        </PrivateRoute>
      } />

      {/* ===================== */}
      {/* GOBIERNO CLOUD */}
      {/* ===================== */}
      <Route path="/gobierno-cloud">
        <Route index element={<Navigate to="/gobierno-cloud/cloud-accounts" replace />} />

        <Route path="cloud-accounts" element={
          <PrivateRoute
            requiredArea={AREAS.GOBIERNO_CLOUD}
            requiredPermission="gobierno_cloud.cloud_accounts.view"
          >
            <DashboardLayout>
              <CloudAccountsPage />
            </DashboardLayout>
          </PrivateRoute>
        } />
      </Route>

      {/* ===================== */}
      {/* INNOVATION */}
      {/* ===================== */}
      <Route path="/innovation">
        <Route index element={<Navigate to="/innovation/automatizaciones" replace />} />

        <Route path="dashboard-fusion" element={
          <PrivateRoute
            requiredArea={AREAS.INNOVATION}
            requiredPermission="innovation.dashboard.fusion.view"
          >
            <DashboardLayout><InnovationDashboardFusion /></DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="automatizaciones" element={
          <PrivateRoute
            requiredArea={AREAS.INNOVATION}
            requiredPermission="innovation.automatizaciones.view"
          >
            <DashboardLayout>
              <AutomatizacionesPage />
            </DashboardLayout>
          </PrivateRoute>
        } />

        <Route path="proyectos" element={
          <PrivateRoute
            requiredArea={AREAS.INNOVATION}
            requiredPermission="innovation.proyectos.view"
          >
            <DashboardLayout>
              <ProyectosPage />
            </DashboardLayout>
          </PrivateRoute>
        } />
      </Route>

      {/* ROOT */}
      <Route path="/" element={
        isAuthenticated 
          ? hasRoles
            ? <Navigate to={initialPath} replace />
            : <Navigate to="/pending-approval" replace />
          : <Navigate to="/login" replace />
      } />

      <Route path="*" element={<Navigate to="/" replace />} />

    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}